/**
 * useThrillingLocationsLibrary — 2026-06-10 §3.F wire-up for the canonical
 * Thrilling Locations VG35010 venue-staff NPCs (39).
 *
 * Mirrors useVillainsLibrary.ts: 4-state union + cancelled-flag unmount safety
 * + memoised service stabilisation + per-NPC detail fetch. Tabs are the 6
 * VENUES (casino/hotel/restaurant/train/boat/plane); the drawer filters the
 * npcs array by tab → venue at render time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ThrillingLocationsNpcManifestEntry,
  ThrillingLocationsVenue,
} from '../services/host/ThrillingLocationsServiceModule';
import { ThrillingLocationsServiceModule } from '../services/host/ThrillingLocationsServiceModule';

export type ThrillingLocationsLibraryState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type ThrillingLocationsLibraryTab = ThrillingLocationsVenue;

export interface ThrillingLocationsLibraryHookResult {
  state: ThrillingLocationsLibraryState;
  npcs: ThrillingLocationsNpcManifestEntry[];
  pendingRemainingCount: number;
  error: string | null;
  refresh: () => void;
  activeTab: ThrillingLocationsLibraryTab;
  setActiveTab: (tab: ThrillingLocationsLibraryTab) => void;
  selectedNpcId: string | null;
  selectedNpcData: Record<string, unknown> | null;
  selectedNpcState: ThrillingLocationsLibraryState;
  selectNpc: (id: string | null) => void;
}

export function useThrillingLocationsLibrary(
  thrillingLocationsService?: ThrillingLocationsServiceModule,
): ThrillingLocationsLibraryHookResult {
  const resolvedService = useMemo(
    () => thrillingLocationsService ?? new ThrillingLocationsServiceModule(),
    [thrillingLocationsService],
  );

  const [state, setState] = useState<ThrillingLocationsLibraryState>('idle');
  const [npcs, setNpcs] = useState<ThrillingLocationsNpcManifestEntry[]>([]);
  const [pendingRemainingCount, setPendingRemainingCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] =
    useState<ThrillingLocationsLibraryTab>('casino');

  const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
  const [selectedNpcData, setSelectedNpcData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedNpcState, setSelectedNpcState] =
    useState<ThrillingLocationsLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectNpc = useCallback((id: string | null) => {
    setSelectedNpcId(id);
    if (id === null) {
      setSelectedNpcData(null);
      setSelectedNpcState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setNpcs(result.npcs);
        setPendingRemainingCount(result.pending_remaining_count);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setNpcs([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedNpcId === null) {
      return;
    }
    let cancelled = false;

    setSelectedNpcState('loading');
    setSelectedNpcData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedNpcId);
        if (cancelled) return;
        if (result === null) {
          setSelectedNpcState('error');
          return;
        }
        setSelectedNpcData(result);
        setSelectedNpcState('ready');
      } catch (e) {
        if (cancelled) return;
        // §3.F G1-reviewer fix (rule #19, no silent swallow): the detail-fetch
        // error only flips selectedNpcState; surface the cause in devtools so a
        // network/auth failure is distinguishable from a 404 (which returns null,
        // not a throw — handled above).
        console.error(
          'useThrillingLocationsLibrary: NPC detail fetch failed',
          e,
        );
        setSelectedNpcState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedNpcId]);

  return {
    state,
    npcs,
    pendingRemainingCount,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedNpcId,
    selectedNpcData,
    selectedNpcState,
    selectNpc,
  };
}
